import { useEffect, useState } from "react";
import { ClipboardCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { Field } from "@/components/common";
import { cn } from "@/lib/utils";

const RESULTS = [["ok", "Conforme", "border-emerald-500 bg-emerald-500/15 text-emerald-600"], ["na", "N/A", "border-slate-400 bg-slate-400/15 text-slate-500"], ["ko", "Non conforme", "border-red-500 bg-red-500/15 text-red-500"]];

export function QualityControlDialog({ open, onOpenChange, ro, checklist, canValidate, onDone }) {
  const [items, setItems] = useState([]);
  const [mileage, setMileage] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const qc = ro.quality_control;
  useEffect(() => {
    if (!open) return;
    const base = qc?.items?.length ? qc.items : (checklist || []).map((label) => ({ label, result: "ok", note: "" }));
    setItems(base.map((i) => ({ ...i }))); setMileage(qc?.mileage_out ?? ""); setNote(qc?.note || "");
  }, [open, qc, checklist]);

  const invalid = items.filter((i) => i.result === "na" && !i.note.trim());
  const save = async (validate) => {
    setBusy(true);
    try {
      await api.post(`/repair-orders/${ro.id}/quality-control`, { items, mileage_out: mileage === "" ? null : Number(mileage), note });
      if (validate) await api.post(`/repair-orders/${ro.id}/quality-control/validate`, { note: "" });
      toast.success(validate ? "Contrôle qualité validé — véhicule prêt" : "Checklist enregistrée");
      onOpenChange(false); onDone();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="qc-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-primary" /> Contrôle qualité — {ro.number}</DialogTitle></DialogHeader>
        <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
          {items.map((it, i) => (
            <div key={i} className="rounded-xl border border-border p-3" data-testid={`qc-item-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium">{it.label}</span>
                <div className="flex gap-1">{RESULTS.map(([v, label, cls]) => <button key={v} type="button" onClick={() => setItems((l) => l.map((x, j) => (j === i ? { ...x, result: v } : x)))} className={cn("rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors", it.result === v ? cls : "border-border text-muted-foreground")} data-testid={`qc-item-${i}-${v}`}>{label}</button>)}</div>
              </div>
              {it.result !== "ok" && <Input value={it.note} onChange={(e) => setItems((l) => l.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))} placeholder={it.result === "na" ? "Motif obligatoire (ex. : essai routier impossible, véhicule non roulant)" : "Constat / action corrective"} className="mt-2 h-9" data-testid={`qc-item-${i}-note`} />}
            </div>
          ))}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Kilométrage relevé au contrôle"><Input type="number" value={mileage} onChange={(e) => setMileage(e.target.value)} placeholder={ro.reception?.mileage_in ? `≥ ${ro.reception.mileage_in}` : ""} data-testid="qc-mileage" /></Field>
            <Field label="Remarque générale" className="sm:col-span-2"><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="qc-note" /></Field>
          </div>
        </div>
        {qc?.filled_at && <p className="text-xs text-muted-foreground">Dernière saisie : {qc.filled_by}{qc.validated_at ? ` · validé par ${qc.validated_by}` : " · non validé"}</p>}
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={busy || invalid.length > 0} data-testid="qc-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Enregistrer</Button>
          {canValidate && <Button onClick={() => save(true)} disabled={busy || invalid.length > 0 || items.some((i) => i.result === "ko")} data-testid="qc-validate">Valider le contrôle → véhicule prêt</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
